import {
  type MutableRefObject,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { type ImperativePanelHandle } from '@heidi/design-system/resizable';

import { usePanelContentSlidePins } from './use-panel-content-slide-pins';

type UseProgrammaticPanelResizeParams = {
  leftPanelRef: RefObject<ImperativePanelHandle>;
  rightPanelRef: RefObject<ImperativePanelHandle>;
  containerWidthRef: MutableRefObject<number>;
  panelGroupRef: RefObject<HTMLDivElement>;
  /** How long past the 300ms panel animation a programmatic resize settles. */
  settleMs: number;
};

/**
 * Programmatic (non-user) panel sizing for the sessions split. Owns the settle
 * window that stops open/close/restore animation frames being persisted as a
 * user-chosen width, and the content pins + deferred start that make those
 * resizes read as the RHS session panel's slide/push clip.
 */
export const useProgrammaticPanelResize = ({
  leftPanelRef,
  rightPanelRef,
  containerWidthRef,
  panelGroupRef,
  settleMs,
}: UseProgrammaticPanelResizeParams) => {
  // `onResize` fires asynchronously (ResizeObserver) and the panel animates over
  // 300ms, so open/close/restore emit intermediate frames. We mark a settle
  // window around every programmatic resize and skip persistence during it, so
  // only genuine user resizes (pointer drag OR keyboard) are saved.
  const isProgrammaticResizeRef = useRef(false);
  const programmaticTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const markProgrammaticResize = useCallback(() => {
    isProgrammaticResizeRef.current = true;
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = setTimeout(() => {
      isProgrammaticResizeRef.current = false;
    }, settleMs);
  }, [settleMs]);

  const {
    leftContentRef,
    rightContentRef,
    pinPanelContents,
    schedulePinRelease,
    releasePanelContents,
  } = usePanelContentSlidePins(settleMs);

  // Same width fallback as getSplitPercent — before the ResizeObserver's first
  // report the ref is still 0 while offsetWidth is already real.
  const getContainerPx = useCallback(
    () => containerWidthRef.current || panelGroupRef.current?.offsetWidth || 0,
    [containerWidthRef, panelGroupRef]
  );

  // Starts a pinned panel animation on the SECOND frame after the pins land.
  // Pinning the detail content to its target width forces one expensive note-
  // canvas layout (~hundreds of ms with a heavy note); if the flex change went
  // out in the same style recalc, that layout would land inside the transition
  // window and freeze most of it — the CSS clock keeps running while the main
  // thread is blocked, so the panel would visually jump. Frame 1 absorbs the
  // pin layout (and the collapse-flag React render from the same event); the
  // resize applied on frame 2 then animates over a free main thread. When
  // nothing was pinned there's no layout to absorb, so apply synchronously.
  const animationFrameRef = useRef<number>();
  const beginPinnedResize = useCallback(
    (didPin: boolean, applyResize: () => void) => {
      markProgrammaticResize();
      if (!didPin) {
        applyResize();
        return;
      }
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = undefined;
          // Restart the settle window from the animation's actual start, and
          // release the pins the same interval later.
          markProgrammaticResize();
          schedulePinRelease();
          applyResize();
        });
      });
    },
    [markProgrammaticResize, schedulePinRelease]
  );

  // Applies a programmatic left/right split.
  const applySplitLayout = useCallback(
    (leftPct: number) => {
      const containerPx = getContainerPx();
      const didPin = pinPanelContents({
        leftPx: (containerPx * leftPct) / 100,
        rightPx: (containerPx * (100 - leftPct)) / 100,
      });
      beginPinnedResize(didPin, () => {
        leftPanelRef.current?.resize(leftPct);
        rightPanelRef.current?.resize(100 - leftPct);
      });
    },
    [getContainerPx, pinPanelContents, beginPinnedResize, leftPanelRef, rightPanelRef]
  );

  // Startup sizing runs while the sessions chrome is invisible. Apply the
  // geometry directly, without content pins or the user-facing slide, and
  // mark it ready after the wrapper's resize microtasks drain. That keeps the
  // transition disabled and the chrome covered until the target sizes land.
  const applyInitialSplitLayout = useCallback(
    (leftPct: number, onApplied: () => void) => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      releasePanelContents();
      markProgrammaticResize();
      leftPanelRef.current?.resize(leftPct);
      rightPanelRef.current?.resize(100 - leftPct);
      queueMicrotask(onApplied);
    },
    [leftPanelRef, markProgrammaticResize, releasePanelContents, rightPanelRef]
  );

  // Every programmatic collapse funnels through here so the slide pins and the
  // persistence settle window can't drift apart across call sites.
  const collapseLeftPanel = useCallback(() => {
    const didPin = pinPanelContents({ rightPx: getContainerPx() });
    beginPinnedResize(didPin, () => leftPanelRef.current?.collapse());
  }, [pinPanelContents, getContainerPx, beginPinnedResize, leftPanelRef]);

  useEffect(
    () => () => {
      clearTimeout(programmaticTimerRef.current);
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    },
    []
  );

  return {
    isProgrammaticResizeRef,
    markProgrammaticResize,
    applySplitLayout,
    applyInitialSplitLayout,
    collapseLeftPanel,
    leftContentRef,
    rightContentRef,
  };
};
